import type { FurnitureItem } from "./furniture/types";

export interface FloorplanRoom {
  id: number;
  name: string;
  description: string | null;
  floorName: string;
  addressName: string;
  roomType: string | null;
  polygon: number[][][]; // multi-polygon: [[[x,y], ...], ...]
  labelPos: [number, number] | null;
  isAvailable?: boolean;
  /** A presence sensor says someone is in the room now (the Occupancy layer); absent / null: no reading. */
  inUse?: boolean | null;
  /** Staff payloads only: when it last turned busy (ISO). The member portal never gets it. */
  inUseSince?: string | null;
  isBookable?: boolean;
  isTenantAssignable?: boolean;
  seats?: number | null;
  squareMeters?: number | null;
  hasCamera?: boolean;
  hasTv?: boolean;
  hasSpeaker?: boolean;
  navigationHints?: string;
  currentEvent?: { title: string; start: string; end: string } | null;
  nextFreeSlotStart?: string | null;
  nextFreeSlotDurationMinutes?: number | null;
  occupancies?: {
    id: number;
    tenantId?: number | null;
    tenantName: string;
    startDate: string;
    endDate: string | null;
    /** #428: the lease of several rooms this occupancy belongs to (null = its own lease). The labels come
     *  from the backend (plan R27): "701 +9", "701, 702, …", and this room's "703 (part of 701 +9)". */
    lease?: {
      anchorRoomId: number;
      anchorRoomName: string;
      roomIds: number[];
      labelShort: string;
      labelLong: string;
      labelMember: string;
    } | null;
  }[];
  occupancyStatus?: "occupied" | "ending" | "upcoming" | null;
  occupancyEndDate?: string | null;
  upcomingOccupancies?: { tenantName: string; startDate: string; endDate: string | null }[];
  isNewNeighbour?: boolean;
  isNewMember?: boolean;
  display?: { showName?: boolean; showStatus?: boolean; showIcon?: boolean; label?: string };
  furniture?: FurnitureItem[];
  /** Main room this room is physically merged into (#416). FloorplanViewer folds such parts into their main room. */
  mergedIntoId?: number | null;
  /** Set by foldMergedRooms on a main room: ids of the parts folded into it. Drives the outer-edge-only outline. */
  mergedPartIds?: number[];
}

export interface FloorplanPickerItem {
  id: number;
  name: string;
}

export interface ConnectionZone {
  id: string;
  polygon: number[][];
  name: string;
  type: "elevator" | "stairs" | "entrance" | "corridor";
  floorConnectionId?: number;
  targetFloorplanIds: number[];
  /** Where this connection stops: the plans holding a zone for it (see `withConnectionStops`).
   *  The zone popup offers only these. */
  stops?: ConnectionStop[];
  /** Out of service — directions avoid it; the zone says so. */
  outOfService?: boolean;
  outOfServiceNote?: string;
}

export interface ConnectionStop {
  floorplanId: number;
  /** "Luntmakargatan 26 Floor 5" */
  label: string;
}

/** The `floor_connections` entry both floor plan APIs return (management + member portal). */
export interface ApiFloorConnectionStops {
  id: number;
  is_broken?: boolean;
  broken_note?: string;
  stops?: { floorplan_id: number; label: string }[];
}

/** Attach each zone's stops and out-of-service state from the API's `floor_connections`,
 *  matched on `floorConnectionId`. */
export function withConnectionStops<Z extends ConnectionZone>(
  zones: Z[] | undefined | null,
  connections: ApiFloorConnectionStops[] | undefined | null,
): Z[] {
  const byId = new Map((connections ?? []).map((c) => [c.id, c]));
  return (zones ?? []).map((zone) => {
    const conn = zone.floorConnectionId != null ? byId.get(zone.floorConnectionId) : undefined;
    if (!conn) return zone;
    return {
      ...zone,
      stops: (conn.stops ?? []).map((s) => ({ floorplanId: s.floorplan_id, label: s.label })),
      outOfService: !!conn.is_broken,
      outOfServiceNote: conn.broken_note ?? "",
    };
  });
}

export interface FloorplanFloor {
  floorId: number;
  floorName: string;
  addressId?: number;
  addressName: string;
  region: number[][];
}

export interface FloorplanDoor {
  id: number;
  name: string;
  doorType: string; // "emergency_exit" | "entrance" | etc.
  geometry: { type: "Point"; coordinates: [number, number] }
    | { type: "Polygon"; coordinates: number[][][] }
    | { type: "MultiPolygon"; coordinates: number[][][][] }
    | null;
  arrowSides?: { l?: boolean; r?: boolean; t?: boolean; b?: boolean };
  floorId?: number | null;
}

export interface FloorplanDevice {
  id: number;
  /** str(provider device) — the AP/switch/router's hostname. */
  deviceName: string;
  role: "Access Point" | "Router" | "Access Switch" | string;
  geometry: { type: "Point"; coordinates: [number, number] }
    | { type: "Polygon"; coordinates: number[][][] }
    | { type: "MultiPolygon"; coordinates: number[][][][] }
    | null;
  rotation?: number;
  label?: string;
  floorId?: number | null;
  roomId?: number | null;
}

export interface FloorplanOutlet {
  id: number;
  name: string;
  geometry: { type: "Point"; coordinates: [number, number] } | null;
  floorId?: number | null;
  roomId?: number | null;
  networkId?: number | null;
  medium?: "copper" | "fibre";
  status?: "connected" | "free" | "down";
}

export interface FloorplanLayer {
  id: string;
  name: string;
  /** Drives which renderer paints this layer (rooms / doors / desks /
   *  furniture / equipment / connections / navigation / heatmap_*). */
  type: string;
  /** Initial visibility (on/off) for users who can see this layer.
   *  Ignored when alwaysShow is true. */
  defaultOn: boolean;
  /** When true the layer renders unconditionally — no toggle, can't be
   *  turned off (safety-critical layers like emergency exits). */
  alwaysShow: boolean;
  order: number;
  filter?: Record<string, unknown>;
  /** Optional FK to another layer's id. Children render indented under
   *  their parent in the panel; the parent's master toggle acts as
   *  select-all/deselect-all over its children. */
  parentId?: number | null;
}

export interface FloorplanViewerProps {
  svg: string;
  rooms: FloorplanRoom[];
  selectedRoomId?: number | null;
  onRoomClick?: (room: FloorplanRoom | null) => void;
  /** Fired when a furniture/desk/equipment item is clicked. Item id is prefixed
   *  with `desk-`, `equip-`, or `furn-` followed by the backend row id. */
  onItemClick?: (item: FurnitureItem) => void;
  /** SVG units per meter — for furniture rendering */
  scale?: number;
  /** Called when view (pan/zoom) changes */
  onViewChange?: (view: { x: number; y: number; z: number }) => void;
  /** Floorplan picker — list of available floorplans + active ID */
  floorplans?: FloorplanPickerItem[];
  activeFloorplanId?: number;
  onFloorplanChange?: (id: number) => void;
  onAddressCreate?: (floorId: number) => void;
  onRoomCreate?: (floorId: number, addressId?: number) => void;
  /** Called when an address label on the floorplan is clicked */
  onAddressLabelClick?: (addressName: string, floorId: number) => void;
  /** Connection zones (elevator, stairs) — clickable areas for floor switching */
  connectionZones?: ConnectionZone[];
  /** Floor/address data for address picker + zoom to region */
  floors?: FloorplanFloor[];
  /** Room search */
  searchable?: boolean;
  onSearchResult?: (roomId: number | null) => void;
  /** Floor-level furniture not tied to any room (common areas). */
  floorFurniture?: FurnitureItem[];
  /** Doors on the linked floors — rendered as Point icons + arrow heads
   *  in normal mode, and emergency-exit-coloured zones in emergency mode. */
  doors?: FloorplanDoor[];
  /** Outlets on the linked floors — rendered as port-glyph icons,
   *  auto-clustered when their on-screen radius would overlap. */
  outlets?: FloorplanOutlet[];
  /** Placed network devices (AP / switch / router) on the linked floors.
   *  Role-gated server-side: the floorplan detail ships only the kinds the
   *  caller's available `devices` layer instances name. */
  devices?: FloorplanDevice[];
  /** Click on a device marker. */
  onDeviceClick?: (device: FloorplanDevice) => void;
  /** Backend-driven layer registry. The viewer's panel iterates this list
   *  filtered by visibility state (kept in localStorage). When omitted,
   *  legacy hardcoded toggles are used. */
  availableLayers?: FloorplanLayer[];
  /** Show management-only details (ending dates, upcoming tenants) */
  showManagementDetails?: boolean;
  /** Hide the layers/legend panel (e.g. on tenant portal) */
  hidePanel?: boolean;
  /** Sync view state to URL query params (?fp=&room=&x=&y=&z=) */
  syncUrl?: boolean;
  /** Rendered inside the SVG element (for SVG overlays like navigation routes) */
  svgOverlay?: React.ReactNode;
  /** SVG text elements stripped during import — rendered as an overlay layer */
  svgTextElements?: string;
  /** Navigation paths — array of polylines [[x,y],...] */
  navigationPaths?: number[][][];
  /** #428: rooms drawn with a dashed stroke alongside the selection (the selected room's lease rooms,
   *  or a member's own rooms in the portal). Unset = unchanged rendering. */
  relatedRoomIds?: number[];
  /** Alarm state per location drawn on this plan (backend `iot.floor_alarm`,
   *  `alarm_regions`; map with `toAlarmLocations`). Only the matching regions are
   *  coloured (`resolveAlarmRegions`): armed → red, pre_alarm → orange; never the whole plan. */
  alarmLocations?: FloorplanAlarmLocation[] | null;
  children?: React.ReactNode;
}

export type FloorAlarmState = "armed" | "pre_alarm" | "off" | "unknown";

export interface FloorAlarm {
  state: FloorAlarmState;
  /** ISO time the state started, when known. */
  since?: string | null;
}

/** One location with its own alarm links, as the backend lists it for a plan. */
export interface FloorplanAlarmLocation {
  locationType: "floor" | "address" | "room";
  locationId: number;
  state: FloorAlarmState;
  since?: string | null;
  warningMinutes?: number | null;
}

/** A region the viewer colours: a floor's region or a room's polygon(s). */
export interface FloorplanAlarmRegion {
  key: string;
  locationType: "floor" | "room";
  locationId: number;
  state: "armed" | "pre_alarm";
  label: string;
  /** Rings in plan coordinates. */
  polygons: number[][][];
  /** Set when a floor shows its address's alarm (the floor has no links of its own). */
  inheritedFrom?: "address";
}
